import { useState } from "react";
import { Comment, Phase, Tab, User } from "../types";

interface Props {
  phase: Phase;
  user: User | null;
  theoryComments: Comment[];
  discussionComments: Comment[];
  onAuthRequired: () => void;
}

export default function CommentsSection({
  phase,
  user,
  theoryComments,
  discussionComments,
  onAuthRequired,
}: Props) {
  const [activeTab, setActiveTab] = useState<Tab>("theories");
  const [commentText, setCommentText] = useState("");
  const [userComments, setUserComments] = useState<Comment[]>([]);

  const handlePost = () => {
    if (!commentText.trim()) return;
    if (!user) {
      onAuthRequired();
      return;
    }
    setUserComments((prev) => [
      {
        id: Date.now(),
        author: user.username,
        time: "just now",
        avatar: user.avatar,
        text: commentText.trim(),
        delay: 0,
        isUser: true,
      },
      ...prev,
    ]);
    setCommentText("");
  };

  const isDiscussionLocked = activeTab === "discussion" && phase !== "revealed";
  const seedComments = activeTab === "theories" ? theoryComments : discussionComments;

  return (
    <div className="comments-section">
      <div className="comments-tabs">
        <button
          className={`tab-btn ${activeTab === "theories" ? "active" : ""}`}
          onClick={() => setActiveTab("theories")}
        >
          Theories {phase !== "revealed" && "(spoiler free)"}
        </button>
        <button
          className={`tab-btn ${activeTab === "discussion" ? "active" : ""}`}
          onClick={() => setActiveTab("discussion")}
        >
          {phase === "revealed" ? "Post-reveal" : "🔒 Post-reveal"}
        </button>
      </div>

      {isDiscussionLocked ? (
        <div className="spoiler-warning">
          <p>Solve the mystery first to unlock the discussion.</p>
        </div>
      ) : (
        <>
          {/* Input */}
          <div className="comment-input-wrap">
            <div className="comment-avatar">
              {user ? user.avatar : "?"}
            </div>
            <div style={{ flex: 1 }}>
              <textarea
                className="comment-input"
                placeholder={
                  user
                    ? activeTab === "theories"
                      ? "Drop your theory. No spoilers."
                      : "What gave it away for you?"
                    : "Sign up to join the discussion..."
                }
                value={commentText}
                onClick={() => { if (!user) onAuthRequired(); }}
                onChange={(e) => user && setCommentText(e.target.value)}
                readOnly={!user}
              />
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  className="comment-submit"
                  onClick={handlePost}
                >
                  {user ? "Post" : "Sign up to post"}
                </button>
              </div>
            </div>
          </div>

          {/* User comments */}
          {userComments.map((c) => (
            <CommentRow key={c.id} comment={c} isUser />
          ))}

          {/* Seed comments */}
          {seedComments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
        </>
      )}
    </div>
  );
}

interface CommentRowProps {
  comment: Comment;
  isUser?: boolean;
}

function CommentRow({ comment, isUser }: CommentRowProps) {
  return (
    <div className="comment" style={{ animationDelay: `${comment.delay}s` }}>
      <div
        className="comment-avatar"
        style={isUser ? { background: "rgba(200,151,58,0.3)" } : undefined}
      >
        {comment.avatar}
      </div>
      <div className="comment-body">
        <div className="comment-meta">
          <span className="comment-author">{comment.author}</span>
          <span className="comment-time">{comment.time}</span>
        </div>
        <p className="comment-text">{comment.text}</p>
      </div>
    </div>
  );
}
